import type { Heartbeat, PlayerManifest } from '@tbcast/shared';
import type { PlayerConfig } from './config';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

export interface QueuedCommand {
  id: string;
  command: string;
  args?: Record<string, unknown>;
}

export class PlayerApi {
  token: string | null = null;
  constructor(private cfg: PlayerConfig) {}

  private headers(extra: Record<string, string> = {}): Record<string, string> {
    return { ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}), ...extra };
  }

  private async req(
    path: string,
    init: RequestInit & { timeoutMs?: number } = {},
  ): Promise<Response> {
    const res = await fetch(`${this.cfg.apiUrl}${path}`, {
      ...init,
      signal: AbortSignal.timeout(init.timeoutMs ?? 15_000),
      cache: 'no-store',
    });
    if (res.status === 401) throw new ApiError(401, 'unauthorized');
    return res;
  }

  async startPairing(
    platform: string,
  ): Promise<{ code: string; secret: string; expiresAt: string; pairUrl: string }> {
    const res = await this.req('/api/player/pairing', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ platform, userAgent: navigator.userAgent.slice(0, 300) }),
    });
    if (!res.ok) throw new ApiError(res.status, 'pairing failed');
    return res.json();
  }

  async pairingStatus(
    code: string,
    secret: string,
  ): Promise<{ status: 'pending' | 'paired' | 'expired' | 'unknown'; token?: string }> {
    const res = await this.req(
      `/api/player/pairing/status?${new URLSearchParams({ code, secret })}`,
    );
    if (res.status === 404) return { status: 'unknown' };
    if (res.status === 410) return { status: 'expired' };
    return res.json();
  }

  /** Returns null when the manifest is unchanged (304). */
  async manifest(
    etag: string | null,
  ): Promise<{ manifest: PlayerManifest; etag: string | null } | null> {
    if (this.cfg.demo) {
      const res = await this.req(`/api/player/demo-manifest?variant=${this.cfg.demoVariant}`);
      if (!res.ok) throw new ApiError(res.status, 'demo manifest failed');
      return { manifest: await res.json(), etag: null };
    }
    if (this.cfg.previewScreenId && this.cfg.previewToken) {
      const res = await this.req(
        `/api/player/preview-manifest?${new URLSearchParams({ screen: this.cfg.previewScreenId, pt: this.cfg.previewToken })}`,
      );
      if (!res.ok) throw new ApiError(res.status, 'preview manifest failed');
      return { manifest: await res.json(), etag: null };
    }
    const res = await this.req('/api/player/manifest', {
      headers: this.headers(etag ? { 'If-None-Match': etag } : {}),
    });
    if (res.status === 304) return null;
    if (!res.ok) throw new ApiError(res.status, 'manifest failed');
    return { manifest: await res.json(), etag: res.headers.get('ETag') };
  }

  async heartbeat(hb: Heartbeat): Promise<{ commands: QueuedCommand[]; serverTime: string }> {
    const res = await this.req('/api/player/heartbeat', {
      method: 'POST',
      headers: this.headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(hb),
    });
    if (!res.ok) throw new ApiError(res.status, 'heartbeat failed');
    return res.json();
  }

  async proofOfPlay(
    events: Array<{
      contentKind: string;
      contentId: string;
      contentName?: string;
      startedAt: string;
      durationMs: number;
    }>,
  ): Promise<void> {
    const res = await this.req('/api/player/pop', {
      method: 'POST',
      headers: this.headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ events }),
    });
    if (!res.ok) throw new ApiError(res.status, 'pop failed');
  }

  async screenshot(blob: Blob): Promise<void> {
    const res = await this.req('/api/player/screenshot', {
      method: 'POST',
      headers: this.headers({ 'Content-Type': blob.type || 'image/jpeg' }),
      body: blob,
      timeoutMs: 30_000,
    });
    if (!res.ok) throw new ApiError(res.status, 'screenshot failed');
  }

  async frameCheck(url: string): Promise<boolean> {
    try {
      const res = await this.req(`/api/player/frame-check?url=${encodeURIComponent(url)}`, {
        headers: this.headers(),
        timeoutMs: 8000,
      });
      if (!res.ok) return true;
      return ((await res.json()) as { embeddable: boolean }).embeddable;
    } catch {
      return true;
    }
  }

  async feed(url: string): Promise<string> {
    const pt = this.cfg.previewToken ? `&pt=${encodeURIComponent(this.cfg.previewToken)}` : '';
    const res = await this.req(`/api/player/rss?url=${encodeURIComponent(url)}${pt}`, {
      headers: this.headers(),
      timeoutMs: 12_000,
    });
    if (!res.ok) throw new ApiError(res.status, 'feed failed');
    return res.text();
  }
}
